import { Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { EnumProductType } from "gql/generated";
import { formatPrice, getMultilingualString } from "shared/utils";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { CollapsableStep, UpsellSelectionCard } from "widgets/booker/shared";
import { useEventPurchaseFlow } from "./useEventPurchaseFlow";

export const EventQuantityStep = () => {
  const { t } = useTranslation("booker", { keyPrefix: "PURCHASE" });
  const { flow, sellingConfig, bookableSessions, availabilityBySessionId } =
    useEventPurchaseFlow();
  const { product, language } = useBookerContext();
  const { cart, addCartItem, updateCartItem, removeCartItem } = useCartContext();

  if (flow !== "quantity" || !product) return null;

  const mainCartItem = cart.find(item => item.productId === product._id);
  const quantity = mainCartItem?.quantity ?? 0;

  // Cap by the (single) session's remaining capacity so we can't oversell.
  const soleSession = bookableSessions[0];
  const availability = soleSession
    ? availabilityBySessionId[soleSession.sessionId]
    : undefined;
  const remaining =
    !availability || availability.isUnlimited
      ? undefined
      : Math.max(availability.remainingCapacity ?? 0, quantity);
  const maxPerPurchase = product.eventMetadata?.maxTicketsPerPurchase ?? undefined;
  const maxQuantity =
    remaining != null && maxPerPurchase != null
      ? Math.min(remaining, maxPerPurchase)
      : (remaining ?? maxPerPurchase);
  const isSinglePick = maxPerPurchase === 1;

  const setQuantity = (nextQuantity: number) => {
    if (nextQuantity === 0) {
      removeCartItem(product._id);
      return;
    }

    if (mainCartItem) {
      updateCartItem(product._id, { quantity: nextQuantity });
      return;
    }

    addCartItem({
      productId: product._id,
      productName: getMultilingualString(product.name, language),
      productType: EnumProductType.Event,
      startDateISO: soleSession?.dateISO ?? "",
      endDateISO: soleSession?.dateISO ?? "",
      guestsPerTier: [],
      total: sellingConfig.basePrice * nextQuantity,
      quantity: nextQuantity,
      // The event may also have past sessions, so the server can't infer this.
      ...(soleSession ? { sessionId: soleSession.sessionId } : {}),
    });
  };

  return (
    <CollapsableStep
      title={t("TITLE")}
      complete={quantity > 0}
      stepId="event-quantity"
    >
      <Text color="text.mid">{t("SUBTITLE")}</Text>
      <UpsellSelectionCard
        mainText={t("TITLE")}
        subText={`$${formatPrice(sellingConfig.basePrice)}`}
        quantityEnabled={!isSinglePick}
        quantity={quantity}
        minQuantity={0}
        maxQuantity={maxQuantity}
        checked={quantity > 0}
        disabled={isSinglePick && quantity === 0 && maxQuantity === 0}
        onCheckedChange={on => setQuantity(on ? 1 : 0)}
        onQuantityChange={setQuantity}
      />
    </CollapsableStep>
  );
};
